import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, downloadPdf as downloadPdfFile } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, Plus, Download, MessageCircle, Search, FileText, AlertCircle, CreditCard, X, Clock, Check, Receipt, Users } from "lucide-react";
import { CONCEPTS, money, whatsappLink } from "@/lib/cobranza";
import { PLAN_KEYS, PLAN_LABELS, planAmount, hasAnyPlans } from "@/lib/plans";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import RecibosPanel from "@/components/app/RecibosPanel";

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
const thisMonth = () => new Date().toISOString().slice(0, 7);

// "Nivel · Grado/Grupo" para mostrar junto al nombre del alumno.
const studentTag = (s) => [s?.level, s?.grade].map((x) => (x || "").trim()).filter(Boolean).join(" · ");

function Card({ children, accent = "bg-navy", testid }) {
  return (
    <div className="bg-white border border-nardo-200 rounded-md p-6 relative" data-testid={testid}>
      <div className={`absolute top-0 left-0 h-[2px] w-16 ${accent}`} />
      {children}
    </div>
  );
}

// Searchable student picker (by name, grade or NFC UID)
function StudentPicker({ students, value, onChange, testid = "student-picker" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const selected = students.find((s) => s.id === value);
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return students.slice(0, 50);
    return students.filter((s) =>
      (s.full_name || "").toLowerCase().includes(t) ||
      (s.grade || "").toLowerCase().includes(t) ||
      (s.nfc_uid || "").toLowerCase().includes(t)
    ).slice(0, 50);
  }, [students, q]);

  return (
    <div className="relative" data-testid={testid}>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2 top-2.5 text-nardo-400" />
        <input
          data-testid={`${testid}-input`}
          value={open ? q : (selected ? `${selected.full_name}${studentTag(selected) ? ` · ${studentTag(selected)}` : ""}` : q)}
          onFocus={() => { setOpen(true); setQ(""); }}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder="Buscar alumno por nombre, grado o UID…"
          className="w-full border border-nardo-200 rounded-md pl-8 pr-8 py-2 text-sm text-navy outline-none focus:border-navy"
        />
        {selected && (
          <button type="button" onClick={() => { onChange(""); setQ(""); }} className="absolute right-2 top-2 text-nardo-400 hover:text-navy" data-testid={`${testid}-clear`}><X className="w-4 h-4" /></button>
        )}
      </div>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-nardo-200 rounded-md shadow-lg">
            {matches.length === 0 ? (
              <div className="px-3 py-2 text-sm text-nardo-500">Sin coincidencias</div>
            ) : matches.map((s) => (
              <button
                type="button"
                key={s.id}
                data-testid={`${testid}-option-${s.id}`}
                onClick={() => { onChange(s.id); setOpen(false); setQ(""); }}
                className="w-full text-left px-3 py-2 hover:bg-nardo-50 border-b border-nardo-100 last:border-0"
              >
                <div className="text-sm text-navy font-medium truncate">{s.full_name}</div>
                <div className="text-[11px] text-nardo-500 truncate">{studentTag(s) || "—"}{s.nfc_uid ? ` · UID ${s.nfc_uid}` : ""}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function AdeudosPanel({ schoolName, students }) {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onlyDebt, setOnlyDebt] = useState(true);
  const [q, setQ] = useState("");
  const [charge, setCharge] = useState(null); // fila a cobrar
  const [chMethod, setChMethod] = useState("Efectivo");
  const [chAmount, setChAmount] = useState("");
  const [chSaving, setChSaving] = useState(false);
  const [chAll, setChAll] = useState(false); // cobrar todos los meses vencidos de un jalón
  const navigate = useNavigate();
  const load = async () => {
    try { const r = await api.get("/payments/debts", { params: { month, tz } }); setData(r.data); }
    catch { toast.error("No se pudieron cargar los adeudos"); }
  };
  useEffect(() => { load(); }, [month]); // eslint-disable-line
  const downloadPdf = async () => {
    setBusy(true);
    try {
      await downloadPdfFile("/reports/debts-pdf", { params: { month, tz }, filename: `adeudos_${month}.pdf` });
      toast.success("Reporte de adeudos generado");
    } catch { toast.error("No se pudo generar el reporte"); }
    finally { setBusy(false); }
  };
  const reminderUrl = (r) => {
    const phone = (r.tutor_whatsapp || "").replace(/[^0-9]/g, "");
    if (!phone) return null;
    const bd = Array.isArray(r.due_breakdown) ? r.due_breakdown : [];
    const totalDue = r.total_due != null ? r.total_due : r.balance;
    const vencido = r.overdue || bd.some((m) => m.overdue);
    const lines = [
      `Estimado tutor de ${r.student_name}:`,
      `Le recordamos su ${vencido ? "saldo vencido" : "saldo pendiente"} de colegiatura en ${schoolName || "el colegio"}.`,
    ];
    if (bd.length > 0) {
      lines.push("", "Meses pendientes:");
      bd.forEach((m) => lines.push(`• ${m.label}: ${money(m.balance)}${m.overdue ? " (vencido)" : ""}`));
      lines.push("", `Total a pagar: ${money(totalDue)}.`);
    } else {
      if (r.early_discount > 0) lines.push(`Descuento por pago anticipado aplicado: ${money(r.early_discount)}.`);
      if (r.late_fee > 0) lines.push(`Incluye recargo por mora: ${money(r.late_fee)}.`);
      lines.push(`${vencido ? "Saldo vencido" : "Adeudo"}: ${money(r.balance)}.`);
    }
    lines.push("Agradecemos su pronto pago. ¡Gracias!");
    return `https://wa.me/${phone}?text=${encodeURIComponent(lines.join("\n"))}`;
  };
  const remind = (r) => {
    const url = reminderUrl(r);
    if (!url) { toast.error("El alumno no tiene WhatsApp de tutor en su expediente"); return; }
    window.open(url, "_blank");
  };
  // Deudor = debe el mes O acumula adeudo de meses anteriores del ciclo.
  const isDebtor = (r) => (r.total_due != null ? r.total_due > 0.009 : r.status !== "paid") || r.status !== "paid";
  const remindAll = () => {
    const debtors = (data?.rows || []).filter(isDebtor);
    const withWa = debtors.filter((r) => reminderUrl(r));
    if (withWa.length === 0) { toast.error("No hay deudores con WhatsApp de tutor registrado"); return; }
    withWa.forEach((r, i) => setTimeout(() => window.open(reminderUrl(r), "_blank"), i * 600));
    const noWa = debtors.length - withWa.length;
    toast.success(`Abriendo ${withWa.length} recordatorio(s) de WhatsApp${noWa > 0 ? ` · ${noWa} sin WhatsApp` : ""}`);
  };
  const badge = (r) => {
    const s = typeof r === "string" ? r : r.status;
    const td = typeof r === "object" && r ? (r.total_due || 0) : 0;
    if (s === "paid" && td > 0.009) return { cls: "bg-amber-100 text-amber-700", txt: "Debe meses" };
    return s === "paid"
      ? { cls: "bg-emerald-100 text-emerald-700", txt: "Pagado" }
      : s === "en_revision" ? { cls: "bg-sky-100 text-sky-700", txt: "En revisión" }
      : s === "partial" ? { cls: "bg-orange-100 text-orange-700", txt: "Parcial" } : { cls: "bg-red-100 text-red-700", txt: "Pendiente" };
  };

  const openCharge = (r) => { setCharge(r); setChMethod("Efectivo"); setChAmount(String(r.balance)); setChAll((r.due_months || 0) > 1); };
  const assignPlan = async (studentId, plan) => {
    try {
      await api.put(`/students/${studentId}/plan`, { plan });
      toast.success(plan ? "Plan asignado" : "Plan quitado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo asignar el plan"); }
  };
  const doCharge = async () => {
    setChSaving(true);
    try {
      if (chAll && Array.isArray(charge.due_breakdown) && charge.due_breakdown.length > 0) {
        // Cobrar TODOS los meses vencidos de un jalón: un pago por cada periodo.
        let count = 0, total = 0;
        for (const m of charge.due_breakdown) {
          if (!(m.balance > 0.009)) continue;  // meses cubiertos por saldo a favor: no se cobran
          const { data: pay } = await api.post("/payments", {
            student_id: charge.student_id, concept: "Colegiatura", period: m.period,
            amount: m.balance, discount: 0, apply_scholarship: false, method: chMethod,
            note: `Cobro de adeudo · ${m.label}`,
          });
          count += 1; total += Number(pay.net_amount || 0);
        }
        toast.success(`${count} mes(es) cobrado(s) · ${money(total)}`);
        setCharge(null);
        await load();
      } else {
        const amt = Number(chAmount);
        if (!(amt > 0)) { toast.error("Importe inválido"); setChSaving(false); return; }
        const { data: pay } = await api.post("/payments", {
          student_id: charge.student_id, concept: "Colegiatura", period: month,
          amount: amt, discount: 0, apply_scholarship: false, method: chMethod, note: "Cobro de adeudo",
        });
        toast.success(`Pago registrado · ${pay.folio} · ${money(pay.net_amount)}`);
        setCharge(null);
        await load();
        const s = (students || []).find((x) => x.id === charge.student_id);
        const tutorWa = s?.billing?.tutors?.[0]?.whatsapp;
        const link = whatsappLink(pay, tutorWa, schoolName);
        if (link) window.open(link, "_blank");
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo registrar el pago"); }
    finally { setChSaving(false); }
  };

  const rows = (data?.rows || [])
    .filter((r) => (onlyDebt ? isDebtor(r) : true))
    .filter((r) => { const t = q.trim().toLowerCase(); return !t || (r.student_name || "").toLowerCase().includes(t) || (r.grade || "").toLowerCase().includes(t); });

  return (
    <Card accent="bg-red-500" testid="adeudos-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-display font-semibold text-lg text-navy flex items-center gap-2"><AlertCircle className="w-4 h-4 text-red-500" /> Alumnos con adeudos · Colegiaturas</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <input data-testid="debts-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="border border-nardo-200 rounded-md px-2 py-1.5 text-sm text-navy outline-none focus:border-navy" />
          <button data-testid="debts-remind-all" onClick={remindAll} disabled={!data || data.debtors === 0} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"><MessageCircle className="w-3.5 h-3.5" /> Recordatorio masivo</button>
          <button data-testid="debts-pdf" onClick={downloadPdf} disabled={busy} className="inline-flex items-center gap-1.5 border border-navy/30 text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-50"><Download className="w-3.5 h-3.5" /> {busy ? "…" : "PDF"}</button>
        </div>
      </div>
      {data?.not_billable && (
        <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700 font-medium" data-testid="debts-no-billing">
          Este mes no se cobra colegiatura (fuera del periodo de cobro configurado en Ajustes → Reglas de colegiatura).
        </div>
      )}
      {data?.due_day && !data?.not_billable && (
        <p className="text-[11px] text-nardo-500 mb-3" data-testid="debts-due-day">Fecha límite de pago: día {data.due_day} de cada mes de cobro.</p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="border border-nardo-100 rounded-md p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Esperado (mes)</div><div className="font-display font-extrabold text-xl text-navy mt-1">{data ? money(data.totals.expected) : "—"}</div></div>
        <div className="border border-nardo-100 rounded-md p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Cobrado (mes)</div><div className="font-display font-extrabold text-xl text-emerald-700 mt-1">{data ? money(data.totals.collected) : "—"}</div></div>
        <div className="border border-nardo-100 rounded-md p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Adeudo (mes)</div><div className="font-display font-extrabold text-xl text-red-600 mt-1">{data ? money(data.totals.overdue) : "—"}</div></div>
        <div className="border-2 border-red-300 bg-red-50 rounded-md p-3" data-testid="debts-total-cycle"><div className="text-[10px] uppercase tracking-[0.2em] text-red-600">Adeudo total (ciclo)</div><div className="font-display font-extrabold text-xl text-red-700 mt-1">{data && data.totals.total_due != null ? money(data.totals.total_due) : "—"}</div></div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-nardo-400" />
          <input data-testid="debts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar alumno o grado…" className="w-full border border-nardo-200 rounded-md pl-8 pr-3 py-2 text-sm text-navy outline-none focus:border-navy" />
        </div>
        <div className="inline-flex rounded-md border border-nardo-200 overflow-hidden text-xs shrink-0" data-testid="debts-filter-toggle">
          <button onClick={() => setOnlyDebt(true)} data-testid="debts-filter-only" className={`px-3 py-2 font-medium transition-colors ${onlyDebt ? "bg-navy text-white" : "text-nardo-600 hover:bg-nardo-50"}`}>Solo con adeudo{data ? ` (${data.debtors})` : ""}</button>
          <button onClick={() => setOnlyDebt(false)} data-testid="debts-filter-all" className={`px-3 py-2 font-medium transition-colors ${!onlyDebt ? "bg-navy text-white" : "text-nardo-600 hover:bg-nardo-50"}`}>Todos</button>
        </div>
      </div>

      {!data || rows.length === 0 ? (
        <p className="text-sm text-nardo-500">{!data ? "Cargando…" : onlyDebt ? "🎉 No hay alumnos con adeudo este mes." : "No hay alumnos con colegiatura configurada. Define la colegiatura en el expediente de cada alumno."}</p>
      ) : (
        <div className="divide-y divide-nardo-100 max-h-[26rem] overflow-y-auto">
          {rows.map((r) => {
            const b = badge(r);
            const canCharge = r.balance > 0 && r.status !== "en_revision";
            return (
              <div key={r.student_id} className="py-2.5 flex items-center justify-between gap-2" data-testid="debt-row">
                <div className="min-w-0">
                  <div className="text-sm text-navy font-medium truncate">{r.student_name} <span className="text-[11px] text-nardo-400">· {studentTag(r) || r.grade}</span>{r.overdue && <span className="ml-1.5 text-[9px] uppercase tracking-wider bg-red-100 text-red-700 px-1.5 py-0.5 rounded" data-testid={`debt-overdue-${r.student_id}`}>Vencido</span>}</div>
                  <div className="text-[11px] text-nardo-500">Esperado {money(r.expected)} · Pagado {money(r.paid)}{r.pending > 0 ? ` · En revisión ${money(r.pending)}` : ""} · Adeudo <span className={r.balance > 0 ? "text-red-600 font-semibold" : ""}>{money(r.balance)}</span></div>
                  {r.total_due != null && r.due_months > 1 && (
                    <div className="text-[11px] font-semibold text-red-700" data-testid={`debt-total-cycle-${r.student_id}`}>⚠ Adeudo total del ciclo: {money(r.total_due)} · {r.due_months} meses sin pagar</div>
                  )}
                  {r.credit_balance > 0.009 && (
                    <div className="text-[11px] font-semibold text-emerald-700" data-testid={`debt-credit-${r.student_id}`}>Saldo a favor disponible: {money(r.credit_balance)}</div>
                  )}
                  {Array.isArray(r.due_breakdown) && r.due_breakdown.some((m) => m.credit_applied > 0.009) && (
                    <div className="text-[10px] text-emerald-600">
                      {r.due_breakdown.filter((m) => m.credit_applied > 0.009).map((m) => `Se aplicó ${money(m.credit_applied)} de saldo a favor a ${m.label}`).join(" · ")}
                    </div>
                  )}
                  {(r.late_fee > 0 || r.early_discount > 0) && (
                    <div className="text-[10px] text-nardo-400">{r.late_fee > 0 ? `Recargo por mora: ${money(r.late_fee)}` : ""}{r.late_fee > 0 && r.early_discount > 0 ? " · " : ""}{r.early_discount > 0 ? `Descuento anticipado: ${money(r.early_discount)}` : ""}</div>
                  )}
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-[10px] text-nardo-400">Plan:</span>
                    <select value={r.plan || ""} onChange={(e) => assignPlan(r.student_id, e.target.value)} data-testid={`debt-plan-${r.student_id}`} className="text-[11px] border border-nardo-200 rounded px-1.5 py-0.5 bg-white text-navy outline-none focus:border-navy">
                      <option value="">Sin plan</option>
                      <option value="basico">Básico</option>
                      <option value="medio">Medio tiempo</option>
                      <option value="completo">Tiempo completo</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${b.cls}`}>{b.txt}</span>
                  {canCharge && (
                    <button onClick={() => openCharge(r)} className="inline-flex items-center gap-1 bg-navy hover:bg-navy-light text-white px-2.5 py-1 rounded-md text-[11px] font-medium" title="Registrar pago del adeudo" data-testid={`debt-cobrar-${r.student_id}`}><CreditCard className="w-3.5 h-3.5" /> Cobrar</button>
                  )}
                  {r.status !== "paid" && (
                    <button onClick={() => remind(r)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" title="Recordatorio por WhatsApp" data-testid={`debt-remind-${r.student_id}`}><MessageCircle className="w-4 h-4" /></button>
                  )}
                  <button onClick={() => navigate(`/students/${r.student_id}/expediente`)} className="p-1.5 text-nardo-500 hover:text-navy hover:bg-nardo-50 rounded" title="Ir al expediente" data-testid={`debt-open-${r.student_id}`}><Wallet className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog: cobrar adeudo rápido */}
      <Dialog open={!!charge} onOpenChange={(o) => !o && setCharge(null)}>
        <DialogContent data-testid="dialog-cobrar">
          <DialogHeader><DialogTitle>Cobrar adeudo · {charge?.student_name}</DialogTitle></DialogHeader>
          {charge && (
            <div className="space-y-4">
              {charge.due_months > 1 && (
                <div className="rounded-md border border-nardo-200 overflow-hidden" data-testid="cobrar-mode">
                  <button onClick={() => setChAll(true)} data-testid="cobrar-mode-all" className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${chAll ? "bg-navy text-white" : "bg-white text-navy hover:bg-nardo-50"}`}>
                    <span>Cobrar <b>todos los meses vencidos</b> ({charge.due_months})</span>
                    <span className="font-bold">{money(charge.total_due)}</span>
                  </button>
                  <button onClick={() => setChAll(false)} data-testid="cobrar-mode-single" className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between border-t border-nardo-200 ${!chAll ? "bg-navy text-white" : "bg-white text-navy hover:bg-nardo-50"}`}>
                    <span>Cobrar solo el mes ({month})</span>
                    <span className="font-bold">{money(charge.balance)}</span>
                  </button>
                </div>
              )}
              {chAll && charge.due_months > 1 ? (
                <div className="bg-nardo-50 rounded-md p-3 text-sm space-y-1" data-testid="cobrar-breakdown">
                  {(charge.due_breakdown || []).map((m) => (
                    <div key={m.period}>
                      <div className="flex justify-between text-nardo-600">
                        <span className="capitalize">{m.label}{m.balance <= 0.009 && m.credit_applied > 0.009 ? " · cubierto" : (m.overdue ? " · vencido" : "")}</span><span>{money(m.balance)}</span>
                      </div>
                      {m.credit_applied > 0.009 && <div className="text-[11px] text-emerald-600">Se aplicó {money(m.credit_applied)} de saldo a favor</div>}
                    </div>
                  ))}
                  {charge.credit_balance > 0.009 && <div className="flex justify-between text-emerald-700 text-[12px]"><span>Saldo a favor disponible</span><span>{money(charge.credit_balance)}</span></div>}
                  <div className="flex justify-between text-navy font-bold border-t border-nardo-200 pt-1"><span>Total a cobrar</span><span>{money(charge.total_due)}</span></div>
                </div>
              ) : (
                <div className="bg-nardo-50 rounded-md p-3 text-sm space-y-1">
                  <div className="flex justify-between text-nardo-600"><span>Colegiatura {month}</span><span>{money(charge.expected)}</span></div>
                  <div className="flex justify-between text-nardo-600"><span>Pagado</span><span>{money(charge.paid)}</span></div>
                  <div className="flex justify-between text-navy font-bold border-t border-nardo-200 pt-1"><span>Adeudo actual</span><span>{money(charge.balance)}</span></div>
                </div>
              )}
              {!chAll && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Importe a cobrar</label>
                  <input data-testid="cobrar-amount" type="number" min="0" step="0.01" value={chAmount} onChange={(e) => setChAmount(e.target.value)} className="w-full border border-nardo-200 rounded-md px-3 py-2 text-sm text-navy outline-none focus:border-navy" />
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Método</label>
                <select data-testid="cobrar-method" value={chMethod} onChange={(e) => setChMethod(e.target.value)} className="w-full border border-nardo-200 rounded-md p-2 text-sm text-navy bg-white outline-none focus:border-navy">
                  {["Efectivo", "Transferencia", "Tarjeta", "Depósito"].map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <button data-testid="cobrar-submit" onClick={doCharge} disabled={chSaving} className="w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50">
                <Check className="w-4 h-4" /> {chSaving ? "Registrando…" : (chAll && charge.due_months > 1 ? `Cobrar ${charge.due_months} meses · ${money(charge.total_due)}` : "Registrar pago y generar comprobante")}
              </button>
              <p className="text-[11px] text-nardo-400">El pago se registra al instante, se vincula al expediente del alumno y desaparece de esta lista de adeudos.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function Cobranza() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [filterConcept, setFilterConcept] = useState("");
  const [search, setSearch] = useState("");
  const [view, setView] = useState("pago"); // "pago" | "adeudos"

  // PDF dialogs
  const [genOpen, setGenOpen] = useState(false);
  const [genMonth, setGenMonth] = useState(thisMonth());
  const [genBusy, setGenBusy] = useState(false);
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtStudent, setStmtStudent] = useState("");
  const [stmtMonth, setStmtMonth] = useState(thisMonth());
  const [stmtBusy, setStmtBusy] = useState(false);

  // payment form
  const [studentId, setStudentId] = useState("");
  const [concept, setConcept] = useState("Colegiatura");
  const [payPlan, setPayPlan] = useState("");
  const [period, setPeriod] = useState(thisMonth());
  const [amount, setAmount] = useState("");
  const [discount, setDiscount] = useState("");
  const [applyScholarship, setApplyScholarship] = useState(true);
  const [applyEarlyDisc, setApplyEarlyDisc] = useState(true);
  const [method, setMethod] = useState("Efectivo");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const selectedStudent = useMemo(() => students.find((s) => s.id === studentId), [students, studentId]);
  const billing = useMemo(() => selectedStudent?.billing || {}, [selectedStudent]);
  const feeConcepts = useMemo(() => user?.fee_concepts || [], [user]);
  const allConcepts = useMemo(() => {
    const names = [...CONCEPTS];
    feeConcepts.forEach((c) => { const n = (c.name || "").trim(); if (n && !names.includes(n)) names.push(n); });
    return names;
  }, [feeConcepts]);
  const conceptPrice = useMemo(() => {
    const m = {};
    feeConcepts.forEach((c) => { const n = (c.name || "").trim(); if (n) m[n] = Number(c.amount) || 0; });
    return m;
  }, [feeConcepts]);

  const loadStudents = async () => {
    try { const r = await api.get("/students", { params: { kind: "student" } }); setStudents(r.data); } catch {}
  };
  const loadPayments = async () => {
    try {
      const params = {};
      if (filterConcept) params.concept = filterConcept;
      const r = await api.get("/payments", { params });
      setPayments(r.data);
    } catch {}
  };
  useEffect(() => { loadStudents(); }, []);
  useEffect(() => { loadPayments(); }, [filterConcept]); // eslint-disable-line

  const [pending, setPending] = useState([]);
  const loadPending = async () => {
    try { const r = await api.get("/payments/pending"); setPending(r.data); } catch {}
  };
  useEffect(() => { loadPending(); }, []); // eslint-disable-line
  const verifyPay = async (p) => {
    try { await api.post(`/payments/${p.id}/verify`); toast.success("Pago verificado y aplicado"); loadPending(); loadPayments(); }
    catch { toast.error("Error al verificar"); }
  };
  const rejectPay = async (p) => {
    try { await api.post(`/payments/${p.id}/reject`); toast.success("Comprobante rechazado"); loadPending(); }
    catch { toast.error("Error"); }
  };

  useEffect(() => {
    if (!selectedStudent) return;
    if (concept === "Colegiatura") {
      const p = billing.plan || "";
      setPayPlan(p);
      const planAmt = p ? planAmount(user, selectedStudent.level, p, selectedStudent.grade) : 0;
      const amt = planAmt || Number(billing.monthly_fee) || 0;
      setAmount(amt ? String(amt) : "");
    } else if (concept === "Inscripción" && billing.enrollment_fee) setAmount(String(billing.enrollment_fee));
    else if (conceptPrice[concept] > 0) setAmount(String(conceptPrice[concept]));
  }, [studentId, concept]); // eslint-disable-line

  const onPlanChange = (val) => {
    setPayPlan(val);
    const planAmt = val ? planAmount(user, selectedStudent?.level, val, selectedStudent?.grade) : 0;
    const amt = planAmt || Number(billing.monthly_fee) || 0;
    setAmount(amt ? String(amt) : "");
  };

  // Importe BASE del plan (antes de descuentos), para el botón "Usar importe del plan".
  const baseAmount = useMemo(() => {
    if (concept === "Colegiatura") {
      const pa = payPlan ? planAmount(user, selectedStudent?.level, payPlan, selectedStudent?.grade) : 0;
      return pa || Number(billing.monthly_fee) || 0;
    }
    if (concept === "Inscripción") return Number(billing.enrollment_fee) || 0;
    return conceptPrice[concept] || 0;
  }, [concept, payPlan, selectedStudent, billing, user, conceptPrice]);

  // Descuento por pago anticipado (pronto pago) configurado en Ajustes. Se aplica
  // automáticamente (checkbox marcado) y el usuario puede desmarcarlo por pago.
  const earlyDisc = useMemo(() => {
    const off = { pct: 0, amount: 0, active: false };
    // El descuento por pronto pago queda disponible para Dirección Y para el perfil de cobranza (staff).
    if (concept !== "Colegiatura") return off;
    const r = user?.tuition_rules || {};
    if (!r.early_discount_enabled) return off;
    const gross = Math.max(0, Number(amount) || 0);
    if (gross <= 0) return off;
    // El 2% de pronto pago se calcula sobre el monto YA con beca aplicada (neto).
    const schPct = Number(billing.scholarship_pct) || 0;
    const schAmt = applyScholarship ? (gross * schPct) / 100 : 0;
    const base = Math.max(0, gross - schAmt);
    if (base <= 0) return off;
    const type = r.early_discount_type === "fixed" ? "fixed" : "pct";
    const val = Number(r.early_discount_value) || 0;
    const amt = Math.min(base, type === "pct" ? (base * val) / 100 : val);
    if (!(amt > 0)) return off;
    const pct = type === "pct" ? val : (base > 0 ? (amt / base) * 100 : 0);
    return { pct: Math.round(pct * 100) / 100, amount: Math.round(amt * 100) / 100, active: true };
  }, [concept, amount, user, applyScholarship, billing]);

  const preview = useMemo(() => {
    const amt = Math.max(0, Number(amount) || 0);
    const manual = Math.max(0, Number(discount) || 0);
    const early = applyEarlyDisc ? earlyDisc.amount : 0;
    const schPct = Number(billing.scholarship_pct) || 0;
    const sch = applyScholarship && concept === "Colegiatura" ? (amt * schPct) / 100 : 0;
    return { amt, manual, early, sch, schPct, net: Math.max(0, amt - manual - early - sch) };
  }, [amount, discount, earlyDisc, applyEarlyDisc, applyScholarship, concept, billing]);

  const register = async (e) => {
    e.preventDefault();
    if (!studentId) { toast.error("Selecciona un alumno"); return; }
    if (!(Number(amount) > 0)) { toast.error("Ingresa un importe válido"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/payments", {
        student_id: studentId, concept, period: concept === "Colegiatura" ? period : null,
        amount: Number(amount), discount: Number(discount) || 0,
        early_discount: applyEarlyDisc ? (earlyDisc.amount || 0) : 0,
        early_discount_pct: applyEarlyDisc ? (earlyDisc.pct || 0) : 0,
        apply_scholarship: applyScholarship, method, note,
      });
      toast.success(`Pago registrado · ${data.folio} · ${money(data.net_amount)}`);
      setAmount(""); setDiscount(""); setNote("");
      loadPayments();
      const tutorWa = billing.tutors?.[0]?.whatsapp;
      const link = whatsappLink(data, tutorWa, user?.school_name);
      if (link) window.open(link, "_blank");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al registrar el pago");
    } finally { setSaving(false); }
  };

  const downloadReceipt = async (p) => {
    try {
      await downloadPdfFile(`/payments/${p.id}/receipt`, { filename: `comprobante_${p.folio}.pdf` });
    } catch { toast.error("No se pudo descargar el comprobante"); }
  };

  const sendWhatsapp = (p) => {
    const s = students.find((x) => x.id === p.student_id);
    const tutorWa = s?.billing?.tutors?.[0]?.whatsapp;
    const link = whatsappLink(p, tutorWa, user?.school_name);
    if (!link) { toast.error("El alumno no tiene WhatsApp de tutor en su expediente"); return; }
    window.open(link, "_blank");
  };

  const downloadGeneralMonthly = async () => {
    setGenBusy(true);
    try {
      await downloadPdfFile("/reports/income-pdf", { params: { period: "month", month: genMonth, tz }, filename: `pagos_general_${genMonth}.pdf` });
      toast.success("PDF mensual general generado");
      setGenOpen(false);
    } catch { toast.error("No se pudo generar el PDF"); }
    finally { setGenBusy(false); }
  };

  const downloadStudentStatement = async () => {
    if (!stmtStudent) { toast.error("Selecciona un alumno"); return; }
    setStmtBusy(true);
    try {
      const s = students.find((x) => x.id === stmtStudent);
      await downloadPdfFile(`/students/${stmtStudent}/payment-statement-pdf`, { params: { month: stmtMonth, tz }, filename: `pagos_${(s?.full_name || "alumno").replace(/\s+/g, "_")}_${stmtMonth}.pdf` });
      toast.success("PDF de pagos por alumno generado");
      setStmtOpen(false);
    } catch { toast.error("No se pudo generar el PDF"); }
    finally { setStmtBusy(false); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return payments.filter((p) => !q || (p.student_name || "").toLowerCase().includes(q) || (p.folio || "").toLowerCase().includes(q));
  }, [payments, search]);

  const toolBtn = (active) =>
    `inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
      active ? "bg-navy text-white" : "border border-navy/30 text-navy hover:bg-navy hover:text-white"
    }`;

  return (
    <div className="max-w-7xl mx-auto" data-testid="cobranza-page">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.3em] text-navy-light mb-3 flex items-center gap-2"><span className="w-8 h-px bg-navy" /> Finanzas</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-navy flex items-center gap-3"><Wallet className="w-8 h-8 text-navy-light" strokeWidth={1.5} /> Cobranza</h1>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="cobranza-toolbar">
        <button data-testid="btn-realizar-pago" onClick={() => setView("pago")} className={toolBtn(view === "pago")}>
          <CreditCard className="w-4 h-4" /> Realizar pago
        </button>
        <button data-testid="btn-adeudos" onClick={() => setView("adeudos")} className={toolBtn(view === "adeudos")}>
          <AlertCircle className="w-4 h-4" /> Adeudos
        </button>
        <button data-testid="btn-planes" onClick={() => setView("planes")} className={toolBtn(view === "planes")}>
          <Users className="w-4 h-4" /> Reporte por plan
        </button>
        <button data-testid="btn-verificar" onClick={() => { setView("verificar"); loadPending(); }} className={toolBtn(view === "verificar")}>
          <Clock className="w-4 h-4" /> Por verificar{pending.length > 0 ? ` (${pending.length})` : ""}
        </button>
        <button data-testid="btn-recibos" onClick={() => setView("recibos")} className={toolBtn(view === "recibos")}>
          <Receipt className="w-4 h-4" /> Recibos de pago{pending.length > 0 ? ` (${pending.length})` : ""}
        </button>
        <button data-testid="btn-pdf-general" onClick={() => setGenOpen(true)} className={toolBtn(false)}>
          <Download className="w-4 h-4" /> PDF mensual de pagos general
        </button>
        <button data-testid="btn-pdf-alumno" onClick={() => setStmtOpen(true)} className={toolBtn(false)}>
          <FileText className="w-4 h-4" /> PDF de pagos por alumno
        </button>
      </div>

      {view === "adeudos" ? (
        <AdeudosPanel schoolName={user?.school_name} students={students} />
      ) : view === "planes" ? (
        <PlanesReport />
      ) : view === "recibos" ? (
        <RecibosPanel onPendingChange={loadPending} onChanged={() => { loadPayments(); loadPending(); }} />
      ) : view === "verificar" ? (
        <Card testid="pending-verify-card">
          <h3 className="font-display font-semibold text-lg text-navy mb-1">Transferencias por verificar</h3>
          <p className="text-sm text-nardo-500 mb-4">Comprobantes subidos por los padres. Revisa que el depósito llegó a tu banco y confírmalo.</p>
          {pending.length === 0 ? (
            <p className="text-sm text-nardo-400 py-6 text-center">No hay pagos pendientes de verificación. 🎉</p>
          ) : (
            <div className="divide-y divide-nardo-100">
              {pending.map((p) => (
                <div key={p.id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid="pending-row">
                  <div className="min-w-0">
                    <div className="text-navy font-medium truncate">{p.student_name} · {p.concept}{p.period ? ` · ${p.period}` : ""}</div>
                    <div className="text-[11px] text-nardo-400">{p.created_at ? `Registrado: ${new Date(p.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""} · Transferencia</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-navy font-semibold">${Number(p.net_amount).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span>
                    {p.proof_token && <a href={`${API}/payment-proof/${p.proof_token}`} target="_blank" rel="noreferrer" data-testid={`view-proof-${p.id}`} className="p-1.5 text-navy hover:bg-nardo-50 rounded" title="Ver comprobante"><FileText className="w-4 h-4" /></a>}
                    <button onClick={() => verifyPay(p)} data-testid={`verify-${p.id}`} className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md text-xs font-medium"><Check className="w-3.5 h-3.5" /> Verificar</button>
                    <button onClick={() => rejectPay(p)} data-testid={`reject-${p.id}`} className="inline-flex items-center gap-1 border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-md text-xs font-medium"><X className="w-3.5 h-3.5" /> Rechazar</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Registrar pago */}
          <Card testid="cobranza-register-card">
            <h3 className="font-display font-semibold text-lg text-navy mb-4">Registrar pago</h3>
            <form onSubmit={register} className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Alumno</label>
                <StudentPicker students={students} value={studentId} onChange={setStudentId} testid="pay-student" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="pay-concept" value={concept} onChange={(e) => setConcept(e.target.value)} className="w-full border border-nardo-200 rounded-md p-2 text-sm text-navy bg-white outline-none focus:border-navy">
                  {allConcepts.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {concept === "Colegiatura" ? (
                  <input data-testid="pay-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy" />
                ) : <div />}
              </div>
              {concept === "Anualidad" && (
                <p data-testid="pay-anualidad-note" className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                  Al registrar la <strong>Anualidad</strong>, este alumno queda excluido del cobro mensual de Colegiatura durante el ciclo escolar vigente (se evita el cobro doble).
                </p>
              )}
              {concept === "Colegiatura" && hasAnyPlans(user) && (
                <div data-testid="pay-plan-field">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Plan de pago</label>
                  <select data-testid="pay-plan" value={payPlan} onChange={(e) => onPlanChange(e.target.value)} disabled={!selectedStudent}
                    className="w-full border border-nardo-200 rounded-md p-2 text-sm text-navy bg-white outline-none focus:border-navy disabled:opacity-50">
                    <option value="">Colegiatura del alumno{selectedStudent && billing.monthly_fee ? ` · ${money(billing.monthly_fee)}/mes` : ""}</option>
                    {PLAN_KEYS.map((k) => { const amt = planAmount(user, selectedStudent?.level, k, selectedStudent?.grade); return (
                      <option key={k} value={k}>{PLAN_LABELS[k]}{amt > 0 ? ` · ${money(amt)}/mes` : ""}</option>
                    ); })}
                  </select>
                  <p className="text-[11px] text-nardo-400 mt-1">Al elegir un plan se toma su importe según el nivel y grado del alumno.</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className={user?.role === "staff" ? "col-span-2" : ""}>
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Importe base (sin descuentos)</label>
                    {baseAmount > 0 && (
                      <button type="button" data-testid="pay-reset-base" onClick={() => setAmount(String(baseAmount))} className="text-[10px] font-semibold text-navy hover:underline">Usar importe del plan ({money(baseAmount)})</button>
                    )}
                  </div>
                  <input data-testid="pay-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy" />
                  {concept === "Colegiatura" && baseAmount > 0 && Number(amount) > 0 && Number(amount) < baseAmount - 0.5 && (
                    <p className="text-[10px] text-orange-600 mt-1" data-testid="pay-base-warn">⚠ Escribe el importe del plan SIN descuentos ({money(baseAmount)}). La beca y el pronto pago se aplican abajo automáticamente.</p>
                  )}
                </div>
                {user?.role !== "staff" && (
                  <div data-testid="pay-discount-field">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Descuento</label>
                    <input data-testid="pay-discount" type="number" min="0" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0.00" className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy" />
                  </div>
                )}
              </div>
              {concept === "Colegiatura" && earlyDisc.active && (
                <label className="flex items-center gap-2 text-sm text-navy">
                  <input data-testid="pay-early-toggle" type="checkbox" checked={applyEarlyDisc} onChange={(e) => setApplyEarlyDisc(e.target.checked)} />
                  Aplicar descuento por pronto pago ({earlyDisc.pct}%)
                </label>
              )}
              {concept === "Colegiatura" && (Number(billing.scholarship_pct) > 0) && (
                <label className="flex items-center gap-2 text-sm text-navy">
                  <input data-testid="pay-scholarship" type="checkbox" checked={applyScholarship} onChange={(e) => setApplyScholarship(e.target.checked)} />
                  Aplicar beca del alumno ({billing.scholarship_pct}%)
                </label>
              )}
              <select data-testid="pay-method" value={method} onChange={(e) => setMethod(e.target.value)} className="w-full border border-nardo-200 rounded-md p-2 text-sm text-navy bg-white outline-none focus:border-navy">
                {["Efectivo", "Transferencia", "Tarjeta", "Depósito"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <input data-testid="pay-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota (opcional)" className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-sm" />

              <div className="bg-nardo-50 rounded-md p-3 text-sm space-y-1" data-testid="pay-preview">
                <div className="flex justify-between text-nardo-600"><span>Importe base</span><span>{money(preview.amt)}</span></div>
                {preview.early > 0 && <div className="flex justify-between text-emerald-700" data-testid="pay-early-disc"><span>Descuento pronto pago ({earlyDisc.pct}%)</span><span>- {money(preview.early)}</span></div>}
                {preview.manual > 0 && <div className="flex justify-between text-nardo-600"><span>Descuento manual</span><span>- {money(preview.manual)}</span></div>}
                {preview.sch > 0 && <div className="flex justify-between text-emerald-700" data-testid="pay-sch-line"><span>Beca ({preview.schPct}%)</span><span>- {money(preview.sch)}</span></div>}
                <div className="flex justify-between items-center border-t border-navy/20 mt-1 pt-2">
                  <span className="text-[11px] uppercase tracking-[0.15em] text-navy font-bold">Importe real a cobrar</span>
                  <span className="font-display font-extrabold text-xl text-navy" data-testid="pay-net">{money(preview.net)}</span>
                </div>
                <p className="text-[10px] text-nardo-400">Este es el importe YA con descuentos. Es el que se registra y aparece en el comprobante.</p>
              </div>

              <button data-testid="pay-submit" type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50">
                <Plus className="w-4 h-4" /> {saving ? "Registrando…" : "Registrar y generar comprobante"}
              </button>
              <p className="text-[11px] text-nardo-400">Al registrar se abre WhatsApp con el comprobante para enviarlo al tutor (si tiene WhatsApp en su expediente).</p>
            </form>
          </Card>

          {/* Pagos recientes */}
          <Card accent="bg-emerald-500" testid="cobranza-list-card">
            <h3 className="font-display font-semibold text-lg text-navy mb-4">Pagos registrados</h3>
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-nardo-400" />
                <input data-testid="pay-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar alumno o folio…" className="w-full border border-nardo-200 rounded-md pl-8 pr-3 py-2 text-sm text-navy outline-none focus:border-navy" />
              </div>
              <select data-testid="pay-filter-concept" value={filterConcept} onChange={(e) => setFilterConcept(e.target.value)} className="border border-nardo-200 rounded-md px-2 py-2 text-sm text-navy bg-white outline-none focus:border-navy">
                <option value="">Todos los conceptos</option>
                {allConcepts.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {filtered.length === 0 ? <p className="text-sm text-nardo-500">Sin pagos.</p> : (
              <div className="divide-y divide-nardo-100 max-h-[26rem] overflow-y-auto">
                {filtered.map((p) => (
                  <div key={p.id} className="py-3 flex items-center justify-between gap-2" data-testid="payment-item">
                    <div className="min-w-0">
                      <div className="text-sm text-navy font-medium truncate">{p.student_name} · <span className="text-emerald-700 font-mono">{money(p.net_amount)}</span></div>
                      <div className="text-[11px] text-nardo-500 truncate">{p.folio} · {p.concept}{p.period ? ` (${p.period})` : ""} · Registrado: {new Date(p.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => downloadReceipt(p)} className="p-1.5 text-nardo-500 hover:text-navy hover:bg-nardo-50 rounded" title="Descargar PDF" data-testid={`pay-download-${p.id}`}><Download className="w-4 h-4" /></button>
                      <button onClick={() => sendWhatsapp(p)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" title="Enviar por WhatsApp" data-testid={`pay-whatsapp-${p.id}`}><MessageCircle className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Dialog: PDF mensual general */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent data-testid="dialog-pdf-general">
          <DialogHeader><DialogTitle>PDF mensual de pagos general</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-nardo-600">Reporte de todos los ingresos del mes, seccionado por conceptos.</p>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Mes</label>
              <input data-testid="gen-month" type="month" value={genMonth} onChange={(e) => setGenMonth(e.target.value)} className="w-full border border-nardo-200 rounded-md px-3 py-2 text-sm text-navy outline-none focus:border-navy" />
            </div>
            <button data-testid="gen-download" onClick={downloadGeneralMonthly} disabled={genBusy} className="w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50">
              <Download className="w-4 h-4" /> {genBusy ? "Generando…" : "Descargar PDF"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: PDF de pagos por alumno */}
      <Dialog open={stmtOpen} onOpenChange={setStmtOpen}>
        <DialogContent data-testid="dialog-pdf-alumno">
          <DialogHeader><DialogTitle>PDF de pagos por alumno</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Alumno</label>
              <StudentPicker students={students} value={stmtStudent} onChange={setStmtStudent} testid="stmt-student" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Mes</label>
              <input data-testid="stmt-month" type="month" value={stmtMonth} onChange={(e) => setStmtMonth(e.target.value)} className="w-full border border-nardo-200 rounded-md px-3 py-2 text-sm text-navy outline-none focus:border-navy" />
            </div>
            <button data-testid="stmt-download" onClick={downloadStudentStatement} disabled={stmtBusy} className="w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50">
              <Download className="w-4 h-4" /> {stmtBusy ? "Generando…" : "Descargar PDF"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PlanesReport() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    api.get("/payments/plans-report").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data) return <Card testid="planes-report-card"><p className="text-sm text-nardo-400 py-6 text-center">Cargando…</p></Card>;
  const cards = [...data.plans, { key: "sin", label: "Sin plan", amount: 0, count: data.sin_plan.count, expected_income: data.sin_plan.expected_income }];
  return (
    <Card testid="planes-report-card">
      <h3 className="font-display font-semibold text-lg text-navy mb-1 flex items-center gap-2"><Users className="w-5 h-5 text-navy-light" /> Alumnos por plan de pago</h3>
      <p className="text-sm text-nardo-500 mb-5">Cuántos alumnos hay en cada plan y el ingreso mensual esperado (colegiatura ya con beca aplicada).</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {cards.map((p) => (
          <div key={p.key} className="border border-nardo-200 rounded-xl p-4" data-testid={`plan-report-${p.key}`}>
            <div className="text-[11px] uppercase tracking-wider text-nardo-400">{p.label}</div>
            <div className="text-3xl font-display font-extrabold text-navy mt-1">{p.count}</div>
            <div className="text-[11px] text-nardo-400 mt-1">alumno(s)</div>
            {p.amount > 0 && <div className="text-[11px] text-nardo-500 mt-2">Importe: {money(p.amount)}</div>}
            <div className="text-sm font-semibold text-emerald-700 mt-1">{money(p.expected_income)}<span className="text-[10px] text-nardo-400 font-normal"> /mes</span></div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between border-t border-nardo-100 pt-4">
        <div className="text-sm text-nardo-600">Total de alumnos: <b className="text-navy">{data.total_students}</b></div>
        <div className="text-sm text-nardo-600">Ingreso mensual esperado: <b className="text-emerald-700">{money(data.total_expected)}</b></div>
      </div>
    </Card>
  );
}
